import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { matchPlayerGroupConfigApi } from '../../../services/api';
import { useNavigate } from 'react-router-dom';

interface MatchPlayerGroupConfig {
  id: number;
  name: string;
  match: {
    id: number;
    name: string;
  };
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

interface PaginationParams {
  page?: number;
  page_size?: number;
  is_system?: boolean;
}

const MatchPlayerGroupConfigList = () => {
  const [configs, setConfigs] = useState<MatchPlayerGroupConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [isSystemOnly, setIsSystemOnly] = useState(true);
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      sortable: true,
      filterable: true,
    },
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 200,
      sortable: true,
      filterable: true,
    },
    { 
      field: 'match',
      headerName: 'Match',
      width: 200,
      valueGetter: (match) => match ?? '',
      sortable: true,
      filterable: true,
    },
    {
      field: 'is_system',
      headerName: 'System Config',
      width: 120,
      type: 'boolean',
      sortable: true,
      filterable: true,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      valueGetter: (params) => new Date(params).toLocaleString(),
      sortable: true,
      filterable: true,
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 180,
      valueGetter: (params) => new Date(params).toLocaleString(),
      sortable: true,
      filterable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<MatchPlayerGroupConfig>) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigate(`/match-player-group-configs/${params.row.id}`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchConfigs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, isSystemOnly]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: page + 1,
        page_size: pageSize,
        is_system: isSystemOnly,
      };
      const response = await matchPlayerGroupConfigApi.getAll(params);
      setConfigs(response.data.results);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      try {
        await matchPlayerGroupConfigApi.delete(id);
        fetchConfigs();
      } catch (error) {
        console.error('Error deleting config:', error);
      }
    }
  };

  const handleSystemFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSystemOnly(event.target.checked);
    setPage(0);
  };

  return (
    <Box sx={{ height: "calc(100vh - 320px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Match Player Group Configurations</Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isSystemOnly}
                onChange={handleSystemFilterChange}
                color="primary"
              />
            }
            label="System Configs Only"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/match-player-group-configs/create')}
            sx={{ ml: 2 }}
          >
            Create New
          </Button>
        </Box>
      </Box>

      <DataGrid
        rows={configs}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
        }}
        pageSizeOptions={[10, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 0.5,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f0f7ff',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'scroll',
          }
        }}
        density="compact"
      />
    </Box>
  );
};

export default MatchPlayerGroupConfigList; 